import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useRevealButton } from '../Hooks/use-reveal-button';
import ShareGameModal from '../features/ShareGameModal/ShareGameModal';
import MobileFinishView from '../features/mobile-finish-view/mobile-finish-view';
import { MobileRoundAnimation } from '../features/mobile-round-animation/mobile-round-animation';
import RevengeInvitationModal from '../features/revenge-invitation-modal/revenge-invitation-modal';
import { GameStatus } from '../game-status';
import { useGameStatusStore } from '../game-status/store/game-status-store';
import { useMobileRoomAudio } from '../mobile/mobile-room-footer/hooks/use-mobile-audio-hook';
import { MobileRoomFooter } from '../mobile/mobile-room-footer/mobile-room-footer';
import { getMintingMessages } from '../mobile/mobile-room-footer/mobile-room-footer.utils';
import { didICaptureTheWin, TECHNICAL_FINISH_STATUSES } from '../mobile/utils/utils';
import { Analytics } from 'lib/AnalyticsLoader/analytics';

import useCreateJoinRoomEvents from 'modules/Room/Hooks/useCreateJoinRoomEvents';
import { useMoveHandler } from 'modules/Room/Hooks/useMoveHandler';
import { PlayMenu } from 'modules/Room/features/play-menu/play-menu';
import { GameStatusEnum, MintingEnum } from 'modules/Room/game-status/constants/game-status-enum';
import MobileGameCard from 'modules/Room/mobile/mobile-game-card/mobile-game-card';

import MobileRoomLayout from 'shared/components/layout/mobile-layout';
import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import MintingLoader from 'shared/components/ui/Loaders/MintingLoader/MintingLoader';
import { SearchingForOpponent } from 'shared/components/ui/Loaders/SearchingForOpponent/SearchingForOpponent';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { RoomStatusEnum, WinnerStatusEnum } from 'shared/constants/shared-enums';
import { lookingForOpponent, waitingToAcceptMessages } from 'shared/constants/text-loaders';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useTransactionReceipt } from 'shared/hooks/useTransactionReceipt';
import { usePlayerGamesStore } from 'shared/store/playerGamesStore';
import { useQuestsStore } from 'shared/store/questsStore';
import { useRoomStore } from 'shared/store/roomStore';
import { useStartGameStore } from 'shared/store/start-game-store';

import ShareIcon from 'assets/share.svg?react';

interface IMobileRoomProps {
  invitedRoomId: number | null;
  handleRevengeRequest: () => void;
  handleAcceptInvite: (roomId: number) => void;
}

export const MobileRoom: FC<IMobileRoomProps> = ({
  invitedRoomId,
  handleRevengeRequest,
  handleAcceptInvite,
}) => {
  const [amIWinner, setAmIWinner] = useState(false);
  const [shareGameModalOpen, setShareGameModalOpen] = useState(false);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const { pointBalance, snapshotPoints } = useQuestsStore();

  const { t } = useTranslation();
  const { chainId, address } = useAccount();
  const { isJoining, isApproving, isStarting } = useStartGameStore();
  const { setCloseHash, closeHash, playMoves } = usePlayerGamesStore();
  const { isSuccess: isSuccessCloseUnusedRoom } = useTransactionReceipt(
    closeHash?.[chainId!]?.hash,
    closeHash?.[chainId!]?.date,
  );

  const publicClient = usePublicClient();
  const walletClient = useWalletClient();

  useCreateJoinRoomEvents();

  const [showRoundAnimation, setShowRoundAnimation] = useState({
    prevRoundCounter: 1,
    show: false,
    lastTime: false,
  });
  const { activeRoomId } = useParams();
  const { resetGameStatus } = useGameStatusStore();

  const {
    playerB,
    winnerStatus,
    amIPlayerA,
    roomStatus,
    playerA,
    showHash,
    roundCounter,
    lastRoundOutcome,
    lastMoves,
    expire,
    roomExpire,
    winsTo,
    exitRoomAction,
  } = useRoomStore();

  const { handleSelectMove } = useMoveHandler();

  const isRoomActive = useMemo(
    () => activeRoomId && playerB.address !== NULL_ADDRESS && roomStatus !== RoomStatusEnum.Closed,
    [activeRoomId, playerB, roomStatus],
  );

  const { gameStatus, mintingLoader, mintingHash } = useGameStatusStore();

  const getMessages = useCallback(() => getMintingMessages(mintingLoader), [mintingLoader]);

  const { showCloseButton, isSuccess, closeUnusedRoomHandler } = useRevealButton('waitYourReveal');

  const toggleShowRoundAnimation = useCallback((flag: boolean) => {
    setShowRoundAnimation((prev) => ({ ...prev, show: flag }));
  }, []);

  const handleExit = useCallback(
    async (activeRoomId: bigint) => {
      if (!activeRoomId || loading) return;

      setLoading(true);
      await exitRoomAction({
        chainId,
        publicClient,
        roomId: activeRoomId,
        userAddress: address,
        walletClient,
      });

      setLoading(false);
      Analytics.takeBetBackEvent(address.substring(2), Number(activeRoomId));
    },
    [loading, chainId, publicClient, activeRoomId, address, walletClient],
  );

  useMobileRoomAudio({
    isGameFinished,
    amIWinner,
    playerBAddress: playerB.address,
    mintingLoader,
    roomStatus,
    isRoomActive,
    roundCounter,
  });

  const isYourMove = useMemo(
    () =>
      gameStatus === GameStatusEnum.WAIT_YOUR_MOVE &&
      !expire &&
      roomStatus !== RoomStatusEnum.Closed &&
      !roomExpire,
    [gameStatus, expire, roomStatus, roomExpire],
  );

  useEffect(() => {
    if (invitedRoomId !== null && amIWinner) {
      setShowInviteModal(true);
    }
  }, [invitedRoomId, amIWinner]);

  useEffect(() => {
    if (isSuccessCloseUnusedRoom) {
      setCloseHash(chainId!, null);
    }
  }, [isSuccessCloseUnusedRoom]);

  useEffect(() => {
    // When the room is closed, mark finished as soon as any round animation has ended.
    if (roomStatus !== RoomStatusEnum.Closed) return;
    const technicalFinish = TECHNICAL_FINISH_STATUSES.includes(winnerStatus);
    const ordinaryFinish = playerB.address !== NULL_ADDRESS && showRoundAnimation.lastTime;
    // For ordinary finishes, wait until the animation is hidden; technical finishes have no round animation.
    if (!technicalFinish && showRoundAnimation.show && !ordinaryFinish) return;
    if (!showRoundAnimation.show) {
      if (technicalFinish || ordinaryFinish) {
        resetGameStatus();
        setIsGameFinished(true);
        setAmIWinner(didICaptureTheWin(winnerStatus, amIPlayerA));
      }
    }
  }, [roomStatus, winnerStatus, amIPlayerA, showRoundAnimation.show, showRoundAnimation.lastTime]);

  useEffect(() => {
    // show replay modal
    const isShowModal =
      (amIPlayerA && winnerStatus === WinnerStatusEnum.WinnerPlayerA) ||
      (!amIPlayerA && winnerStatus === WinnerStatusEnum.WinnerPlayerB);
    if (isShowModal) {
      const timer = setTimeout(() => {
        setShareGameModalOpen(true);
        //longer because we show in this component last round animation
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [winnerStatus, amIPlayerA]);

  useEffect(() => {
    // show animation
    const bigA = BigInt(playerA.wins);
    const bigB = BigInt(playerB.wins);

    const technicalFinish = TECHNICAL_FINISH_STATUSES.includes(winnerStatus);
    const isFinalRound = bigA === BigInt(winsTo) || bigB === BigInt(winsTo);

    const storageKey = `roundAnimation_${activeRoomId}`;
    const lastPlayedRound = Number(sessionStorage.getItem(storageKey) ?? '0');

    const isNormalAnimation =
      roundCounter !== showRoundAnimation.prevRoundCounter &&
      roundCounter !== 1 &&
      lastMoves.round === roundCounter - 1;

    const isFinalAnimation =
      roundCounter === lastMoves.round &&
      roomStatus === RoomStatusEnum.Closed &&
      playerB.address !== NULL_ADDRESS;

    if (
      (isNormalAnimation || isFinalAnimation) &&
      (roundCounter > lastPlayedRound || isFinalRound) &&
      !technicalFinish
    ) {
      setShowRoundAnimation({
        prevRoundCounter: roundCounter,
        show: true,
        lastTime: isFinalRound,
      });

      sessionStorage.setItem(storageKey, String(roundCounter));
    }
  }, [
    activeRoomId,
    roundCounter,
    showRoundAnimation.prevRoundCounter,
    showRoundAnimation.lastTime,
    lastMoves.round,
    roomStatus,
    playerB.address,
    setShowRoundAnimation,
  ]);

  if (showRoundAnimation.show)
    return (
      <MobileRoundAnimation
        pointBalance={Number(pointBalance)}
        prevPoints={Number(snapshotPoints)}
        score={{
          first: Number(amIPlayerA ? playerA.wins : playerB.wins),
          second: Number(amIPlayerA ? playerB.wins : playerA.wins),
        }}
        cards={{
          myMove: amIPlayerA ? lastMoves.playerAMove : lastMoves.playerBMove,
          opponentMove: amIPlayerA ? lastMoves.playerBMove : lastMoves.playerAMove,
        }}
        lastRoundOutcome={lastRoundOutcome}
        setShowRoundAnimation={toggleShowRoundAnimation}
      />
    );

  return (
    <MobileRoomLayout
      className="flex flex-col"
      title={
        <>
          {t('common.liveGame')}
          <div className="h-2 w-2 animate-pulse rounded-[50%] bg-green-dot md:h-4 md:w-4" />
        </>
      }
      footer={
        <MobileRoomFooter
          gameStatus={gameStatus}
          isGameFinished={isGameFinished}
          activeRoomId={activeRoomId}
          address={address}
          chainId={chainId}
          roundCounter={roundCounter}
          expire={expire}
          roomExpire={roomExpire}
          showCloseButton={showCloseButton}
          closeUnusedRoomHandler={closeUnusedRoomHandler}
          playMoves={playMoves}
          amIWinner={amIWinner}
          handleRevengeRequest={handleRevengeRequest}
          isStarting={isStarting}
          isApproving={isApproving}
          roomStatus={roomStatus}
          mintingLoader={mintingLoader}
          isSuccess={isSuccess}
          handleExit={handleExit}
        />
      }
      showFloor={!(isGameFinished && playerB.address !== NULL_ADDRESS)}
    >
      <div className="flex max-h-full grow flex-col">
        <div className="z-50 flex h-full flex-col">
          {isGameFinished &&
          playerB.address !== NULL_ADDRESS &&
          gameStatus !== GameStatusEnum.NO_ONE_JOIN ? (
            <MobileFinishView amIWinner={amIWinner} winnerStatus={winnerStatus} />
          ) : (
            <>
              <MobileGameCard />
              <GameStatus
                isShowEndGameAnimation={
                  (isGameFinished && playerB.address !== NULL_ADDRESS) ||
                  mintingLoader === MintingEnum.YOUR_MOVE ||
                  mintingLoader === MintingEnum.YOUR_REVEAL ||
                  mintingLoader === MintingEnum.TECHNICAL_DEFEAT ||
                  showHash
                }
              />
            </>
          )}
        </div>
        {/* TODO: discuss with Michael how it should look here */}
        {/* {!isGameFinished && <RoomPrizeImage animationsTriggered={false} />} */}

        {isYourMove && (
          <div className="pointer-events-none fixed left-0 right-0 top-0 z-50 h-full w-full bg-gradient-to-b from-transparent to-black"></div>
        )}
      </div>

      <AnimatedTransition
        show={
          roomStatus === RoomStatusEnum.Open &&
          playerA.address === address.toLowerCase() &&
          gameStatus !== GameStatusEnum.NO_ONE_JOIN
        }
        className="pointer-events-none absolute inset-0 z-50 grid place-items-center pt-[7.5rem]"
      >
        <SearchingForOpponent
          className="mx-auto w-full"
          messages={playerB.address !== NULL_ADDRESS ? waitingToAcceptMessages : lookingForOpponent}
          waitingToAccept={playerB.address !== NULL_ADDRESS}
        />
      </AnimatedTransition>

      <AnimatedTransition
        show={mintingLoader === MintingEnum.YOUR_MOVE || mintingLoader === MintingEnum.YOUR_REVEAL}
        className="absolute left-1/2 top-1/2 z-50 flex w-full -translate-x-1/2 -translate-y-1/2 transform justify-center pt-10"
      >
        <MintingLoader className="pt-5" hash={mintingHash} messages={getMessages()} />
      </AnimatedTransition>

      {isYourMove && <PlayMenu selectMove={handleSelectMove} />}
      {((winnerStatus === WinnerStatusEnum.WinnerPlayerA && amIPlayerA) ||
        (winnerStatus === WinnerStatusEnum.WinnerPlayerB && !amIPlayerA)) && (
        <SecondaryBtn
          onClick={() => setShareGameModalOpen(true)}
          svg={<ShareIcon />}
          className="absolute bottom-4 right-0 z-50 -translate-x-1/2 transform"
          childClassName="min-h-12 min-w-12"
          data-testid="share-game-button"
        />
      )}
      <ShareGameModal open={shareGameModalOpen} setOpen={setShareGameModalOpen} isMobile={true} />
      {showInviteModal && (
        <RevengeInvitationModal
          invitedRoomId={invitedRoomId}
          setOpen={() => setShowInviteModal(false)}
          className="absolute bottom-4 left-1/2 z-50 -translate-x-1/2"
          onAcceptInvite={handleAcceptInvite}
          isLoading={isJoining || isApproving}
        />
      )}
    </MobileRoomLayout>
  );
};
